import { useEffect, useState } from 'react';
import { getDocs, updateDoc, doc, collection, deleteDoc } from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import '../styles/global.css';
import { analyticsService } from '../services/analytics';
import nlpService from '../services/nlpService';

// Your existing Story interface
interface Story {
  id?: string;
  text: string;
  xLink?: string;
  status: 'pending' | 'approved';
  createdAt: any;
  category?: string;
}

const AdminPanel = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingStory, setAnalyzingStory] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'approved', 'analytics'
  const [editingStory, setEditingStory] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const auth = getAuth();
  const navigate = useNavigate();

  // Predefined categories for selection
  const availableCategories = [
    'Shared Experience',
    'Treatment Journey',
    'Recovery Story',
    'Alternative Therapy',
    'Personal Insight',
    'Medical Update',
    // Add more categories as needed
  ];

  useEffect(() => {
    fetchStories();
  }, [activeTab]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'stories'));
      const allStories = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Story);
      
      // Filter based on active tab
      const filteredStories = allStories.filter(story => {
        if (activeTab === 'pending') return story.status === 'pending';
        if (activeTab === 'approved') return story.status === 'approved';
        return true; // For any other tab, show all
      }).sort((a, b) => b.createdAt?.toDate()?.getTime() - a.createdAt?.toDate()?.getTime());
      
      setStories(filteredStories);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveStory = async (id: string) => {
    if (window.confirm('Are you sure you want to approve this story?')) {
      try {
        const storyRef = doc(db, 'stories', id);
        await updateDoc(storyRef, { status: 'approved' });
        
        // Track approval in analytics
        await analyticsService.trackStoryApproval(id);
        
        // If on pending tab, remove from list
        if (activeTab === 'pending') {
          setStories(stories.filter(s => s.id !== id));
        } else {
          // Otherwise, refresh list
          fetchStories();
        }
      } catch (error) {
        console.error('Error approving story:', error);
        alert('Failed to approve story.');
      }
    }
  };

  const rejectStory = async (id: string) => {
    if (window.confirm('Are you sure you want to reject this story? This will delete it permanently.')) {
      try {
        const storyRef = doc(db, 'stories', id);
        await deleteDoc(storyRef);
        setStories(stories.filter(s => s.id !== id));
      } catch (error) {
        console.error('Error deleting story:', error);
        alert('Failed to delete story.');
      }
    }
  };

  const analyzeStory = async (story: Story) => {
    if (!story.id) return;
    
    try {
      setAnalyzingStory(story.id);
      
      // First try to analyze with NLP service
      const nlpAnalysis = await nlpService.analyzeStory(story);
      
      if (nlpAnalysis.success) {
        // Save analysis results
        await analyticsService.saveAnalysisResults([story], nlpAnalysis.results, false);
        
        // If there's an X link, analyze that too
        if (story.xLink) {
          // This is a placeholder - you'll implement X analysis later
          console.log(`Analyzing X post: ${story.xLink}`);
        }
        
        alert('Story analysis complete!');
      } else {
        throw new Error(nlpAnalysis.message || 'Analysis failed');
      }
    } catch (error) {
      console.error('Error analyzing story:', error);
      alert(`Failed to analyze story: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAnalyzingStory(null);
    }
  };

  const runBatchAnalysis = async () => {
    if (window.confirm(`Are you sure you want to analyze all ${stories.length} stories? This may take some time.`)) {
      try {
        setLoading(true);
        
        // Run analytics job which will analyze all new stories
        const result = await analyticsService.runAnalyticsJob();
        
        if (result.success) {
          alert(`Successfully analyzed ${result.storiesAnalyzed} stories.`);
        } else {
          throw new Error(result.message || 'Batch analysis failed');
        }
      } catch (error) {
        console.error('Error running batch analysis:', error);
        alert(`Failed to run batch analysis: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    }
  };

    // New function to start editing a story
    const startEditing = (story: Story) => {
      setEditingStory(story.id || null);
      setEditCategory(story.category || '');
    };
  
    // New function to save category changes
    const saveCategory = async (storyId: string) => {
      try {
        const storyRef = doc(db, 'stories', storyId);
        await updateDoc(storyRef, { category: editCategory });
        
        // Update the local state
        setStories(stories.map(s => 
          s.id === storyId ? { ...s, category: editCategory } : s
        ));
        
        // Exit edit mode
        setEditingStory(null);
        setEditCategory('');
        
      } catch (error) {
        console.error('Error updating story category:', error);
        alert('Failed to update category.');
      }
    };
  
    // New function to cancel editing
    const cancelEditing = () => {
      setEditingStory(null);
      setEditCategory('');
    };
  

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = '/admin-login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Admin Panel</h1>
            <button 
              onClick={handleLogout} 
              className="btn btn-secondary logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      
      <main className="container">
        {/* Tab Navigation */}
        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending Stories
          </button>
          <button 
            className={`tab-button ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            Approved Stories
          </button>
          <button 
            className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'analytics' ? (
          <div className="analytics-admin-tab">
            <div className="action-bar">
              <button 
                className="btn btn-primary"
                onClick={runBatchAnalysis}
                disabled={loading}
              >
                {loading ? 'Running Analysis...' : 'Analyze All Stories'}
              </button>
            </div>
            <p className="helper-text">
              This will analyze all stories using the NLP backend and create graphs of relationships
              between treatments, symptoms, and outcomes mentioned in the stories.
            </p>
            {/* You can embed your AnalyticsDashboard component here */}
          </div>
        ) : (
          <>
            <h2>
              {activeTab === 'pending' ? 'Pending' : 'Approved'} Stories ({stories.length})
            </h2>
            
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading stories...</p>
              </div>
            ) : stories.length === 0 ? (
              <div className="card">
                <p>No {activeTab === 'pending' ? 'pending' : 'approved'} stories to display!</p>
              </div>
            ) : (
              <div className="stories-list">
              {stories.map(story => (
                <div key={story.id} className="card story-card">
                  <p className="story-date">
                    {activeTab === 'pending' ? 'Submitted' : 'Approved'}: {story.createdAt?.toDate()?.toLocaleString()}
                  </p>
                  
                  {/* Category display/edit section */}
                  <div className="story-category">
                    {editingStory === story.id ? (
                      <div className="edit-category-form">
                        <label htmlFor="category-select">Category:</label>
                        <select 
                          id="category-select"
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          className="category-select"
                        >
                          <option value="">-- Select Category --</option>
                          {availableCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                        <div className="edit-buttons">
                          <button 
                            onClick={() => saveCategory(story.id!)} 
                            className="btn btn-sm btn-primary"
                          >
                            Save
                          </button>
                          <button 
                            onClick={cancelEditing} 
                            className="btn btn-sm btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="category-display">
                        <span className="label">Category:</span>
                        <span className="value">{story.category || 'Uncategorized'}</span>
                        <button 
                          onClick={() => startEditing(story)} 
                          className="btn btn-sm btn-edit"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <p className="story-text">{story.text}</p>
                    
                    {story.xLink && (
                      <a 
                        href={story.xLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="social-link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        Verify X Post
                      </a>
                    )}
                    
                    <div className="button-group">
                      {activeTab === 'pending' ? (
                        <>
                          <button 
                            onClick={() => approveStory(story.id!)} 
                            className="btn btn-primary"
                          >
                            Approve
                          </button>
                          <button 
                            onClick={() => rejectStory(story.id!)} 
                            className="btn btn-reject"
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            onClick={() => analyzeStory(story)} 
                            className="btn btn-secondary"
                            disabled={analyzingStory === story.id}
                          >
                            {analyzingStory === story.id ? 'Analyzing...' : 'Analyze Story'}
                          </button>
                          {!editingStory && (
                            <button 
                              onClick={() => startEditing(story)} 
                              className="btn btn-primary"
                            >
                              Edit Category
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;